import { useCallback, useEffect, useRef, useState } from "react";
import useDemandStudioEditActions from "./useDemandStudioEditActions";

const useDemandStudioViewState = ({ isOpen, setShowPreview, sectionTexts, caseId, onEditPersisted }) => {
  const [focusedSectionId, setFocusedSectionId] = useState(null);
  const sectionRefs = useRef({});
  const sectionTextsOnFocusRef = useRef(null);
  const { bulkSectionUpdates, isSavingManualEdit } = useDemandStudioEditActions({ caseId, onEditPersisted });

  useEffect(() => {
    if (!isOpen) {
      document.body.classList.remove("demand-studio-modal-open");
      return;
    }
    document.body.classList.add("demand-studio-modal-open");
    return () => {
      document.body.classList.remove("demand-studio-modal-open");
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setShowPreview(false);
  }, [isOpen, setShowPreview]);

  const flush = useCallback(async () => {
    const isDirty = sectionTextsOnFocusRef.current !== null
      && sectionTextsOnFocusRef.current !== sectionTexts;
    sectionTextsOnFocusRef.current = null;
    if(isDirty) {
      await bulkSectionUpdates(sectionTexts);
    }
  }, [sectionTexts]);

  const handleSectionFocus = useCallback((sectionId) => {
    console.log("Focusing section", { sectionId });
    flush();
    sectionTextsOnFocusRef.current = sectionTexts;
    setFocusedSectionId(sectionId);
  }, [sectionTexts, flush]);

  const handleSectionBlur = useCallback(() => {
    flush();
    setFocusedSectionId(null);
  }, [flush]);

  const handleEditSection = useCallback((sectionId) => {
    setFocusedSectionId(sectionId);
    const sectionEl = sectionRefs.current[sectionId];
    if (!sectionEl) return;
    sectionEl.scrollIntoView({ behavior: "smooth", block: "center" });
    const textarea = sectionEl.querySelector("textarea");
    textarea?.focus();
  }, []);

  const handleCardFocus = useCallback((sectionId) => {
    setFocusedSectionId(sectionId);
  }, []);

  const handleJumpToSection = useCallback(
    ({ sectionId, searchTerm, shouldFocusMatch, matchStartIndex }) => {
      setFocusedSectionId(sectionId);
      const sectionEl = sectionRefs.current[sectionId];
      if (!sectionEl) return;
      sectionEl.scrollIntoView({ behavior: "smooth", block: "center" });
      if (!shouldFocusMatch || !searchTerm) return;
      const textarea = sectionEl.querySelector("textarea");
      if (!textarea) return;
      const searchLength = String(searchTerm || "").length;
      if (matchStartIndex < 0 || searchLength <= 0) return;
      textarea.focus();
      textarea.setSelectionRange(matchStartIndex, matchStartIndex + searchLength);
    },
    []
  );

  return {
    focusedSectionId,
    sectionRefs,
    handleSectionFocus,
    handleSectionBlur,
    handleEditSection,
    handleCardFocus,
    handleJumpToSection,
    flushSectionTextUpdates: flush,
    isSavingManualEdit
  };
};

export default useDemandStudioViewState;
